
import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { ResultsLoading } from "@/components/results/ResultsLoading";
import { ResultsError } from "@/components/results/ResultsError";
import { formatMetrics, formatChecklist } from "@/components/results/MetricsFormatter";
import { SEOResults } from "@/components/SEOResults";
import Header from "@/components/Header";
import { AnalysisResult } from "@/types/seo";
import { AnalysisError } from "@/components/results/AnalysisError";
import { supabase } from "@/integrations/supabase/client";

// Dados de exemplo para teste
const mockData: AnalysisResult = {
  id: "6ba70d33-fac0-4603-9b70-6d2c347dfbff",
  created_at: "2025-06-02T11:50:28.297289",
  error: false,
  error_message: null,
  analysis_result: {
    error: false,
    error_message: null,
    keyword_summary: {
      keyword: "marketing digital",
      volume: 110000,
      trend: [
        { year: 2025, month: 1, search_volume: 131513 },
        { year: 2025, month: 2, search_volume: 131513 },
        { year: 2025, month: 3, search_volume: 107159 },
        { year: 2025, month: 4, search_volume: 107159 },
        { year: 2025, month: 5, search_volume: 107159 },
        { year: 2025, month: 6, search_volume: 107159 },
        { year: 2025, month: 7, search_volume: 107159 },
        { year: 2025, month: 8, search_volume: 87675 },
        { year: 2025, month: 9, search_volume: 87675 },
        { year: 2025, month: 10, search_volume: 131513 },
        { year: 2025, month: 11, search_volume: 107159 },
        { year: 2025, month: 12, search_volume: 107159 }
      ]
    },
    url_analyses: [
      {
        url: "https://rockcontent.com/br/blog/marketing-digital/",
        title: "Marketing Digital: o que é, como fazer, estratégias e TUDO sobre!",
        title_length: 65,
        meta_description: "Marketing Digital (mkt digital) é o conjunto de ações online com o objetivo de atrair negócios e fortalecer a marca. Aprenda tudo sobre Marketing Online!",
        description_length: 153,
        content_length: 17596,
        backlinks: 550,
        semrush: {
          backlinks: {
            ascore: 51,
            total: 2223,
            domains_num: 550,
            urls_num: 2235,
            ips_num: 641,
            ipclassc_num: 414,
            follows_num: 2013,
            nofollows_num: 224,
            sponsored_num: 6,
            ugc_num: 0,
            texts_num: 2265,
            images_num: 0,
            forms_num: 0,
            frames_num: 0
          }
        },
        boolean_criteria: {
          title_length_ok: false,
          keyword_in_title: true,
          keyword_in_meta: true,
          keyword_in_title_left: true,
          keyword_in_first_paragraph: true,
          has_single_h1: true,
          keyword_in_h1: true,
          keyword_in_h2: true,
          keyword_in_h3: true,
          clean_url: true,
          keyword_in_url: true,
          is_https: true
        },
        position: 1,
        google_position: 1,
        competitive_metrics: {
          position: 100,
          content_length: 100,
          backlinks: 100,
          referring_domains: 100,
          pagerank: 100
        },
        comparative_score: 100.0,
        checklist_score: 91.66,
        seo_score: 95.83
      },
      {
        url: "https://pt.wikipedia.org/wiki/Marketing_digital",
        title: "Marketing digital – Wikipédia, a enciclopédia livre",
        title_length: 49,
        meta_description: "",
        description_length: 0,
        content_length: 8671,
        backlinks: 542,
        semrush: {
          backlinks: {
            ascore: 56,
            total: 2667,
            domains_num: 542,
            urls_num: 2538,
            ips_num: 749,
            ipclassc_num: 342,
            follows_num: 2403,
            nofollows_num: 265,
            sponsored_num: 2,
            ugc_num: 2,
            texts_num: 2668,
            images_num: 7,
            forms_num: 0,
            frames_num: 0
          }
        },
        boolean_criteria: {
          title_length_ok: true,
          keyword_in_title: true,
          keyword_in_meta: false,
          keyword_in_title_left: true,
          keyword_in_first_paragraph: true,
          has_single_h1: true,
          keyword_in_h1: true,
          keyword_in_h2: true,
          keyword_in_h3: true,
          clean_url: true,
          keyword_in_url: true,
          is_https: true
        },
        position: 2,
        google_position: 2,
        competitive_metrics: {
          position: 75,
          content_length: 25,
          backlinks: 75,
          referring_domains: 75,
          pagerank: 75
        },
        comparative_score: 62.5,
        checklist_score: 83.33,
        seo_score: 72.91
      },
      {
        url: "https://www.organicadigital.com/blog/afinal-como-funciona-o-marketing-digital/",
        title: "Afinal, como funciona o Marketing Digital e como fazê-lo funcionar!",
        title_length: 71,
        meta_description: "Descubra como funciona o marketing digital, suas principais estratégias e técnicas para implementar com sucesso nas empresas.",
        description_length: 134,
        content_length: 5728,
        backlinks: 204,
        semrush: {
          backlinks: {
            ascore: 49,
            total: 336,
            domains_num: 204,
            urls_num: 329,
            ips_num: 238,
            ipclassc_num: 193,
            follows_num: 307,
            nofollows_num: 32,
            sponsored_num: 0,
            ugc_num: 0,
            texts_num: 338,
            images_num: 0,
            forms_num: 0,
            frames_num: 0
          }
        },
        boolean_criteria: {
          title_length_ok: false,
          keyword_in_title: true,
          keyword_in_meta: true,
          keyword_in_title_left: false,
          keyword_in_first_paragraph: true,
          has_single_h1: true,
          keyword_in_h1: true,
          keyword_in_h2: true,
          keyword_in_h3: true,
          clean_url: true,
          keyword_in_url: true,
          is_https: true
        },
        position: 3,
        google_position: 3,
        competitive_metrics: {
          position: 50,
          content_length: 50,
          backlinks: 50,
          referring_domains: 50,
          pagerank: 50
        },
        comparative_score: 50.0,
        checklist_score: 83.33,
        seo_score: 66.66
      },
      {
        url: "https://sebrae.com.br/sites/PortalSebrae/artigos/marketing-digital-para-iniciantes-o-que-e-e-como-funciona,9f1ac3399eeab810VgnVCM1000001b00320aRCRD",
        title: "Marketing digital para iniciantes: o que é e como funciona - Sebrae",
        title_length: 72,
        meta_description: "Sebrae",
        description_length: 6,
        content_length: 8874,
        backlinks: 111,
        semrush: {
          backlinks: {
            ascore: 55,
            total: 174,
            domains_num: 111,
            urls_num: 172,
            ips_num: 142,
            ipclassc_num: 107,
            follows_num: 155,
            nofollows_num: 19,
            sponsored_num: 0,
            ugc_num: 0,
            texts_num: 174,
            images_num: 0,
            forms_num: 0,
            frames_num: 0
          }
        },
        boolean_criteria: {
          title_length_ok: false,
          keyword_in_title: true,
          keyword_in_meta: false,
          keyword_in_title_left: true,
          keyword_in_first_paragraph: false,
          has_single_h1: true,
          keyword_in_h1: false,
          keyword_in_h2: true,
          keyword_in_h3: false,
          clean_url: true,
          keyword_in_url: true,
          is_https: true
        },
        position: 4,
        google_position: 4,
        competitive_metrics: {
          position: 25,
          content_length: 75,
          backlinks: 25,
          referring_domains: 25,
          pagerank: 25
        },
        comparative_score: 37.5,
        checklist_score: 66.66,
        seo_score: 52.08
      }
    ],
    ai_insights: [
      {
        id: 1,
        category: "content",
        title: "Aprimorar título para respeitar limite de caracteres",
        description: "O título atual não respeita o limite de 60 caracteres, o que pode impactar na exibição nos resultados de busca e na experiência do usuário.",
        action: "Reformule o título para que tenha até 60 caracteres, mantendo a palavra-chave.",
        expected_result: "Melhoria na exibição do título nos resultados de busca, potencialmente aumentando a taxa de cliques."
      },
      {
        id: 2,
        category: "content",
        title: "Expandir conteúdo para aumentar a autoridade temática",
        description: "Embora o conteúdo já seja extenso, há uma oportunidade para ser ainda mais abrangente em tópicos relacionados ao marketing digital.",
        action: "Adicione seções que abordem tendências atuais e estudos de caso relevantes em marketing digital.",
        expected_result: "Fortalecimento da autoridade da página e aumento do tempo de permanência dos usuários."
      },
      {
        id: 3,
        category: "positioning",
        title: "Aumentar backlinks para melhorar a autoridade",
        description: "Com 550 backlinks, a URL analisada supera os concorrentes, mas o aumento dessa quantidade pode consolidar ainda mais a posição.",
        action: "Implemente uma estratégia de Digital PR focada em conseguir links de sites relevantes e de alta autoridade.",
        expected_result: "Maior fortalecimento da autoridade da página e resistência a mudanças no ranking."
      },
      {
        id: 4,
        category: "content",
        title: "Melhorar a presença de palavras-chave em H2 e H3",
        description: "Apesar de a palavra-chave estar presente em H2 e H3, uma maior variedade de palavras-chave relacionadas pode ser benéfica.",
        action: "Inclua palavras-chave secundárias e sinônimos nos H2 e H3 para expandir a relevância semântica.",
        expected_result: "Melhora na relevância semântica da página e potencial aumento no tráfego orgânico."
      },
      {
        id: 5,
        category: "technical",
        title: "Refinar a arquitetura do conteúdo para melhor navegação",
        description: "Um conteúdo muito longo pode dificultar a navegação e a retenção do usuário.",
        action: "Adicione um índice ou sumário no início do conteúdo para facilitar a navegação.",
        expected_result: "Melhor experiência do usuário e potencial aumento no tempo de permanência na página."
      },
      {
        id: 6,
        category: "technical",
        title: "Monitorar e ajustar o desempenho de SEO regularmente",
        description: "Com a posição atual em 1, é crucial acompanhar constantemente métricas de SEO para evitar quedas.",
        action: "Estabeleça uma rotina mensal de análise de métricas de SEO e ajuste a estratégia conforme necessário.",
        expected_result: "Manutenção da posição de destaque e adaptação rápida a mudanças no comportamento de busca."
      }
    ]
  }
};

const Results = () => {
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<AnalysisResult | null>(null);
  const [errorState, setErrorState] = useState({
    hasError: false,
    message: "",
    analysisId: "",
    hasShownToast: false
  });
  const [retryCount, setRetryCount] = useState(0);
  const timeoutRef = useRef<NodeJS.Timeout>();
  const abortControllerRef = useRef<AbortController>();

  useEffect(() => {
    const fetchResults = async () => {
      if (!id) {
        setLoading(false);
        setErrorState({
          hasError: true,
          message: "ID inválido",
          analysisId: "",
          hasShownToast: false
        });
        return;
      }

      // Se o ID for 'demo', use os dados de exemplo
      if (id === 'demo') {
        setData(mockData);
        setLoading(false);
        return;
      }

      try {
        console.log(`Fetching results for ID: ${id}`);
        
        const { data: response, error } = await supabase.functions.invoke('get-report', {
          body: { id }
        });

        if (error) {
          throw error;
        }

        if (!response || response.error) {
          throw new Error(response?.error_message || "Erro ao carregar resultados");
        }

        console.log('Response received:', response);
        setData(response);
        setLoading(false);
        setRetryCount(0);
        setErrorState({ 
          hasError: false, 
          message: "", 
          analysisId: "", 
          hasShownToast: false 
        });
      } catch (error) {
        console.error("Error fetching results:", error);
        
        setLoading(false);
        setErrorState({
          hasError: true,
          message: error.message || "Erro ao carregar resultados",
          analysisId: id,
          hasShownToast: false
        });
      }
    };

    fetchResults();

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      abortControllerRef.current?.abort();
    };
  }, [id]);

  useEffect(() => {
    if (errorState.hasError && !errorState.hasShownToast) {
      toast.error("Não foi possível carregar os resultados. Por favor, tente novamente mais tarde.");
      setErrorState(prev => ({ ...prev, hasShownToast: true }));
    }
  }, [errorState.hasError, errorState.hasShownToast]);

  if (loading) {
    return <ResultsLoading />;
  }

  if (errorState.hasError) {
    return <ResultsError analysisId={errorState.analysisId} />;
  }

  // Verifica se há erro no analysis_result
  if (data?.analysis_result?.error) {
    return <AnalysisError 
      message={data.analysis_result.error_message || "Não foi possível analisar a URL"} 
      url={data.analysis_result.url_analyses?.[0]?.url}
    />;
  }

  if (!data?.analysis_result?.url_analyses[0]) {
    return <ResultsError analysisId={id} />;
  }

  const mainAnalysis = data.analysis_result.url_analyses[0];
  const metrics = formatMetrics({ mainAnalysis });
  const checklist = formatChecklist({ mainAnalysis });

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <SEOResults
        score={mainAnalysis.seo_score}
        metrics={metrics}
        checklist={checklist}
        keywordData={data.analysis_result.keyword_summary}
        urlAnalyses={data.analysis_result.url_analyses}
        aiInsights={data.analysis_result.ai_insights}
      />
    </div>
  );
};

export default Results;
